import {
    ALLIANZ,
    ALLIANZ_ESSENTIALS,
    Instalment,
    INTERACTION_PROCESS_CLAIM_IMPACT,
    INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE,
    INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY,
    INTERACTION_PROCESS_NB,
    INTERACTION_PROCESS_RENEWAL,
    Party,
    PaymentCard,
    Policy
} from "@ferry/journey-services";
import * as moment from "moment";

export function capitalize(text: string): string {
    return text.toLowerCase().split(' ').map(s => s.charAt(0).toUpperCase() + s.substring(1).toLowerCase()).join(' ');
}

//
// Retrieves payment card type name
//
export function renderCardTypeName(card: PaymentCard): string {
    if (card.cardType.includes("VISA")) {
        return "Visa";
    }
    if (card.cardType.includes("MASTERCARD")) {
        return "Mastercard";
    }
    return "";
}

export function findCardToReuse(party: Party, reuseCard?: boolean, mainCardId?: number): PaymentCard | undefined {
    const paymentCards: PaymentCard[] = party.paymentCards;

    if (reuseCard) {
        if (paymentCards.length === 1) {
            return paymentCards[0];
        }

        return paymentCards.find(c => c.id === mainCardId)!;
    } else {
        return undefined;
    }
}

export function findMainAnnualPaymentCardByMainCardId(party: Party, mainCardId: string): PaymentCard | null {
    for (const card of party.paymentCards) {
        if (Number(mainCardId) === card.id) {
            return card;
        }
    }
    return null;
}

export function trimFileExtension(filename: string): string {
    return filename.substring(0, filename.lastIndexOf('.'));
}

export function getInputClassName(inputValid?: boolean): string {
    if (inputValid == null) {
        return "form-control";
    } else if (inputValid) {
        return "form-control is-valid";
    } else {
        return "form-control is-invalid";
    }
}

export function setSessionStorage(key: string, value: any): void {
    window.sessionStorage.setItem(key, JSON.stringify(value));
}

export function getSessionStorage(key: string): any {
    const object: string | null = window.sessionStorage.getItem(key);
    if (object) {
        return JSON.parse(object);
    } else {
        return null;
    }
}

export function deleteSessionStorage(key: string): void {
    window.sessionStorage.removeItem(key);
}

export function getAmountWithDecimalDigits(amount: any): string {
    const fixedAmount: number = 1 * amount;
    return new Intl.NumberFormat('en-UK', {style: 'currency', currency: 'GBP'}).format(fixedAmount);
}

export function getAmountWithoutDecimalDigits(amount: number): string {
    return "£" + new Intl.NumberFormat('en-UK', {maximumFractionDigits: 0}).format(amount);
}

export function getAddress(address: any): string {
    const addressLine1: string = address.addressLine1 ? address.addressLine1 + ", " : "";
    const addressLine2: string = address.addressLine2 ? address.addressLine2 + ", " : "";
    const addressLine3: string = address.addressLine3 ? address.addressLine3 + ", " : "";

    return capitalize(addressLine1 + addressLine2 + addressLine3)
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
        .join(",");
}

export function getModifiedMobileNumber(mobileNumber: string): string {
    const stringArray: string [] = mobileNumber.split("");
    const output: string [] = stringArray.map((letter: string, index: number) => {
        let tempString: string = "";
        tempString += letter;
        if (index === 3 || index === 6) {
            tempString += " ";
        }
        return tempString;
    });
    return output.join("");
}

export function downloadDocument(content: ArrayBuffer, docName: string): void {
    const mediaType = "application/pdf";
    const link = document.createElement("a");
    const blob = new Blob([content], {type: mediaType});
    const blobUrl = URL.createObjectURL(blob);
    document.body.appendChild(link);
    link.download = docName;
    link.href = blobUrl;
    link.click();
    document.body.removeChild(link);
}

export function getLink(redirectTo: string, disabled: boolean): string {
    return disabled ? "#" : redirectTo;
}

export function getCursorClassName(disabled: boolean): string {
    return disabled ? "cursor-pointer-default" : "cursor-pointer";
}

export const formatDateTime = (inputDate: string): string | null => {
    return formatDate(inputDate, "D MMMM YYYY, HH:mm")
}

export const formatDateDMY = (inputDate: string): string | null => {
    return formatDate(inputDate, "D MMMM YYYY")
}

export const formatDate = (inputDate: string, format: string) => {
    const valueAsMoment = moment.utc(inputDate, moment.ISO_8601);

    if (valueAsMoment.isValid()) {
        return (valueAsMoment.format(format))
    }

    return null;
};

export const allowToRenderFutureMTAExplainedHeader = (interactionProcess: string): boolean => {
    return interactionProcess !== INTERACTION_PROCESS_NB &&
        interactionProcess !== INTERACTION_PROCESS_MTA_CHANGE_PAYMENT_DAY && interactionProcess !== INTERACTION_PROCESS_MTA_ADD_PAY_OFF_BALANCE &&
        interactionProcess !== INTERACTION_PROCESS_RENEWAL && interactionProcess !== INTERACTION_PROCESS_CLAIM_IMPACT;
};

export function calculateInstallmentPayment(instalments: Instalment[] | undefined): number {
    return instalments && instalments.length > 1 ? instalments[instalments.length - 1].amount : 0;
}

export function findLatestNotFutureMTAPolicyVersion(policies: Policy[], policyNumber: string) {
    const policiesFiltered =  policies.filter(p => p.policyNumber === policyNumber && moment().isAfter(p.interaction.headerInfo.startDate));

    if(policiesFiltered != null && policiesFiltered.length > 0) {

       return policiesFiltered.reduce((latestPolicy, currentPolicy) => {
            return latestPolicy === undefined || moment(currentPolicy.interaction.createdOn)
                .isAfter(moment(latestPolicy.interaction.createdOn))
                ? currentPolicy : latestPolicy;
        }, policiesFiltered[0]);
    }
    return undefined;
}

export function getIsOptionalExtraIncludedText(coverTier: string, optionalExtra: any): string {
    if (coverTier === undefined || coverTier === "" || coverTier === ALLIANZ) {
        return "Included as standard";
    }

    return optionalExtra ? "Included" : "Not included";
}

export function validPolicy(allPolicies: Policy[], policyNumber: string) : Policy | undefined {
    const policy = currentValidPolicy(allPolicies, policyNumber)

    return policy === undefined && allPolicies.length !== 0 ?
        futureValidPolicy(allPolicies, policyNumber) : policy;
}

function currentValidPolicy(allPolicies: Policy[], policyNumber: string) : Policy | undefined {
    return allPolicies
        .filter(policy => moment().isSameOrAfter(policy.interaction.headerInfo.startDate))
        .sort((policyA, policyB) => moment(policyB.interaction.headerInfo.startDate).diff(policyA.interaction.headerInfo.startDate))
        .find(policy => policy.policyNumber === policyNumber);
}

function futureValidPolicy(allPolicies: Policy[], policyNumber: string) : Policy | undefined {
    return [...allPolicies]
        .sort((policyA, policyB) => moment(policyA.interaction.headerInfo.startDate).diff(policyB.interaction.headerInfo.startDate))
        .find(policy => policy.policyNumber === policyNumber);
}

export const getCoverTierName = (coverTier: string): string => {
    switch (coverTier) {
        case ALLIANZ_ESSENTIALS:
            return "Essentials";
        case ALLIANZ:
            return "Plus";
        default:
            return coverTier;
    }
}

export const getDomainUrlForCookies = (hostUri: string) => {
    if (hostUri.includes("athenapaas.com")) {
        return ".athenapaas.com";
    } else if (hostUri.includes("vwfs.co.uk")) {
        return ".vwfs.co.uk";
    } else {
        return hostUri;
    }
}
